import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Download, FileText, Package } from "lucide-react";
import { SectionHeader } from "@/components/shared";
import { GeneratedAsset } from "@/components/GeneratedAsset";
import StillCard from "@/components/StillCard";
import ClipCard from "@/components/ClipCard";
import HandoffDoc from "@/components/HandoffDoc";
import SpaComparison from "@/components/SpaComparison";
import StylePicker from "@/components/StylePicker";
import RoughCut from "@/components/RoughCut";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHASES = [
  ["phase-1", "01", "Character lock"],
  ["phase-2", "02", "UI stills & end card"],
  ["phase-3", "03", "Live-action clips"],
  ["phase-4", "04", "Editor handoff"],
];

export default function Gallery() {
  const [data, setData] = useState(null);
  const [doc, setDoc] = useState(null);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/assets`);
    setData(data);
  }, []);

  useEffect(() => {
    load();
    axios.get(`${API}/handoff`).then((r) => setDoc(r.data));
  }, [load]);

  const generating = data?.assets.some((a) => a.status === "generating") || data?.videos?.some((v) => v.status === "generating") || data?.rough_cut?.status === "generating";
  useEffect(() => {
    if (!generating) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [generating, load]);

  const generate = async (id) => {
    setData((d) => ({ ...d, assets: d.assets.map((a) => (a.id === id ? { ...a, status: "generating" } : a)) }));
    await axios.post(`${API}/generate/${id}`);
    load();
  };

  const generateVideo = async (id) => {
    setData((d) => ({ ...d, videos: d.videos.map((v) => (v.id === id ? { ...v, status: "generating" } : v)) }));
    await axios.post(`${API}/video/${id}`);
    load();
  };

  const onRendered = (id, url) =>
    setData((d) => ({ ...d, stills: d.stills.map((s) => (s.id === id ? { ...s, url, created_at: Date.now() } : s)) }));

  const [styleBusy, setStyleBusy] = useState(false);
  const setStyle = async (spa_style) => {
    setStyleBusy(true);
    setData((d) => ({ ...d, spa_style }));
    await axios.put(`${API}/settings`, { spa_style });
    try {
      const { data: r } = await axios.post(`${API}/render/s3`);
      onRendered("s3", r.url);
    } finally { setStyleBusy(false); }
  };

  const buildCut = async (opts) => {
    setData((d) => ({ ...d, rough_cut: { ...d.rough_cut, status: "generating" } }));
    await axios.post(`${API}/roughcut`, opts);
    load();
  };

  const spaStyle = data?.spa_style || "warm";
  const clipsForStyle = (data?.clips || []).map((c) =>
    c.id === "clip3" && spaStyle === "loft" && c.variant ? { ...c, frame: c.variant.frame, video_prompt: c.variant.video_prompt, title: `${c.title} · loft`, videoId: "clip3_loft" } : { ...c, videoId: c.id });

  const charSheet = data?.assets.find((a) => a.id === "char_sheet");
  const frames = Object.fromEntries((data?.assets || []).filter((a) => a.phase === 3).map((a) => [a.id, a]));
  const videos = Object.fromEntries((data?.videos || []).map((v) => [v.id, v]));
  const model = data?.video_model?.split("/").slice(1, 3).join(" ") || "";

  return (
    <div data-testid="gallery-page" style={{ display: "grid", gridTemplateColumns: "260px 1fr", minHeight: "100vh" }}>
      <aside className="rail" style={{ position: "sticky", top: 0, height: "100vh", padding: "48px 40px", borderRight: "1px solid var(--ink-06)", display: "flex", flexDirection: "column" }}>
        <span className="wordmark" style={{ fontSize: 44 }}>marova</span>
        <div className="eyebrow" style={{ marginTop: 6 }}>30s vertical · production kit</div>
        <a href="/" data-testid="nav-landing" style={{ marginTop: 14, fontSize: 12, fontWeight: 600, color: "var(--ink-60)", textDecoration: "none" }}>← Waitlist landing page</a>
        <nav style={{ marginTop: 64, display: "flex", flexDirection: "column", gap: 18 }}>
          {PHASES.map(([id, n, t]) => (
            <a key={id} href={`#${id}`} data-testid={`nav-${id}`} style={{ display: "flex", gap: 14, alignItems: "baseline", textDecoration: "none", color: "var(--charcoal)", fontSize: 14, fontWeight: 600 }}>
              <span className="serif" style={{ fontSize: 22, color: "var(--burgundy)", fontWeight: 400 }}>{n}</span>{t}
            </a>
          ))}
        </nav>
        <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
          <a className="btn btn-primary" data-testid="download-package" href={`${API}/package.zip`} style={{ justifyContent: "center" }}><Package size={14} />Download package</a>
          <a className="btn btn-ghost" data-testid="download-handoff-md" href={`${API}/handoff.md`} target="_blank" rel="noreferrer" style={{ justifyContent: "center" }}><FileText size={14} />Handoff .md</a>
        </div>
      </aside>

      <main style={{ padding: "0 72px 160px", maxWidth: 1240 }}>
        <header className="fade-up" style={{ paddingTop: 96 }}>
          <div className="eyebrow">Hybrid production package · 9:16 · Manhattan → Aura Med Spa</div>
          <h1 className="serif" style={{ fontSize: 88, fontWeight: 500, lineHeight: .98, margin: "18px 0 0", letterSpacing: "-0.015em", maxWidth: 900 }}>
            Your evening, <em style={{ color: "var(--burgundy)" }}>handled.</em>
          </h1>
          <p style={{ maxWidth: 620, fontSize: 16, lineHeight: 1.65, color: "var(--ink-60)", marginTop: 28 }}>
            Cinematic key frames, brand-accurate UI stills at native screen resolution, a designed end card, and a frame-accurate handoff so an editor can composite the final 30 seconds.
          </p>
        </header>

        <SectionHeader id="phase-1" n="01" kicker="Phase 1" title="Character lock" />
        <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 40, marginTop: 40, alignItems: "start" }}>
          {charSheet && <GeneratedAsset asset={charSheet} onGenerate={generate} ratio="2 / 3" />}
          <div style={{ fontSize: 14, lineHeight: 1.7, color: "var(--ink-60)", paddingTop: 8 }}>
            <p style={{ margin: 0 }}>Locked identity fed into every clip frame as a visual anchor: early-30s, light-olive skin, sleek low bun, camel wool coat, cream silk blouse, black leather tote, natural makeup, gold huggie earrings.</p>
            <p style={{ marginTop: 14 }}>Approve this sheet before generating Phase 3 frames — they reference it directly.</p>
            <details style={{ marginTop: 18 }}><summary style={{ cursor: "pointer", fontWeight: 600, color: "var(--charcoal)" }}>Generation prompt</summary><p data-testid="char-prompt" style={{ fontSize: 13, marginTop: 10 }}>{charSheet?.prompt}</p></details>
          </div>
        </div>

        <SectionHeader id="phase-2" n="02" kicker="Phase 2" title="UI stills & end card" />
        <p style={{ maxWidth: 640, fontSize: 14, lineHeight: 1.65, color: "var(--ink-60)", marginTop: 20 }}>
          Designed in the Marova system (Cormorant Garamond + Inter). Phone stills at 1080×1920, tablet at 1536×2048 — render to PNG for corner-pinning. Maya 6:15 · Lena 7:20 · $165 stays identical everywhere.
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 24, marginTop: 36 }}>
          {data?.stills.map((s) => <StillCard key={s.id} still={s} onRendered={onRendered} spaStyle={spaStyle} />)}
        </div>

        <SectionHeader id="phase-3" n="03" kicker="Phase 3" title="Live-action clips" />
        <p style={{ maxWidth: 640, fontSize: 14, lineHeight: 1.65, color: "var(--ink-60)", marginTop: 20 }}>
          Key frame per clip, anchored to the reference sheet, plus a ready-to-paste video prompt. Each clip can be rendered to an 8-second 9:16 MP4 with Kling v3 Pro (image-to-video, audio off) straight from its key frame. Phones and tablets stay blank in every clip.
        </p>
        <StylePicker value={spaStyle} onChange={setStyle} busy={styleBusy} />
        <div style={{ display: "flex", flexDirection: "column", gap: 24, marginTop: 36 }}>
          {clipsForStyle.map((c) => <ClipCard key={c.id} clip={c} frame={frames[c.frame]} onGenerate={generate} video={videos[c.videoId]} onVideo={generateVideo} model={model} />)}
        </div>
        <SpaComparison clip={data?.clips.find((c) => c.id === "clip3")} frames={frames} videos={videos} onGenerate={generate} onVideo={generateVideo} model={model} />
        <RoughCut cut={data?.rough_cut} onBuild={buildCut} spaStyle={spaStyle} />

        <SectionHeader id="phase-4" n="04" kicker="Phase 4" title="Editor handoff" />
        <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
          <a className="btn btn-ghost" href={`${API}/handoff.md`} target="_blank" rel="noreferrer" data-testid="handoff-md-inline"><Download size={14} />Download HANDOFF.md</a>
        </div>
        <HandoffDoc doc={doc} />
      </main>
    </div>
  );
}
